import { Accordion, Badge, Code, Group, Paper, SimpleGrid, Stack, Text } from '@mantine/core';
import { ReactNode } from 'react';
import CopyableValue from 'src/secret-menu/conversation-debugger/CopyableValue';
import { ConversationDebug } from 'src/secret-menu/conversation-debugger/types';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

const SOURCE_COLORS: Record<string, string> = {
  CHAT: 'blue',
  DOCUMENT_EDIT: 'grape',
};

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack gap={2}>
      <Text size="textXs" c="dimmed">
        {label}
      </Text>
      {children}
    </Stack>
  );
}

function formatDateTime(value: string): string {
  return value ? new Date(value).toLocaleString() : '—';
}

export default function ConversationHeader({ conversation }: { conversation: ConversationDebug }) {
  const model =
    typeof conversation.metadata.last_model === 'string' ? conversation.metadata.last_model : null;
  const household =
    conversation.familyName ??
    (conversation.originFamilyName ? `${conversation.originFamilyName} (origin)` : null);
  const metadataKeys = Object.keys(conversation.metadata);

  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="sm">
        <Group gap="xs" wrap="nowrap">
          <Text size="textSm" fw={FONT_WEIGHTS.semibold} truncate="end" style={{ minWidth: 0 }}>
            {conversation.title ?? '(untitled conversation)'}
          </Text>
          <Badge size="sm" variant="light" color={SOURCE_COLORS[conversation.source] ?? 'gray'}>
            {conversation.source}
          </Badge>
          {model && (
            <Badge size="sm" variant="light" color="violet" style={{ textTransform: 'none' }}>
              {model}
            </Badge>
          )}
        </Group>

        <SimpleGrid cols={{ base: 2, md: 3 }} spacing="sm">
          <Field label="Conversation ID">
            <CopyableValue value={conversation.id} />
          </Field>
          <Field label="Client">
            {conversation.clientId ? (
              <Stack gap={0}>
                <Text size="textXs">{conversation.clientName ?? '(unnamed client)'}</Text>
                <CopyableValue value={conversation.clientId} />
              </Stack>
            ) : (
              <Text size="textXs" c="dimmed">
                —
              </Text>
            )}
          </Field>
          <Field label="Household">
            {household || conversation.familyId ? (
              <Stack gap={0}>
                <Text size="textXs">{household ?? '(unnamed household)'}</Text>
                {conversation.familyId && <CopyableValue value={conversation.familyId} />}
              </Stack>
            ) : (
              <Text size="textXs" c="dimmed">
                —
              </Text>
            )}
          </Field>
          <Field label="User">
            <Stack gap={0}>
              <Text size="textXs">
                {conversation.userName ?? '(unknown user)'}
                {conversation.userEmail ? ` · ${conversation.userEmail}` : ''}
              </Text>
              <CopyableValue value={conversation.userId} />
            </Stack>
          </Field>
          <Field label="Started">
            <Text size="textXs">{formatDateTime(conversation.startedAt)}</Text>
          </Field>
          <Field label="Last updated">
            <Text size="textXs">{formatDateTime(conversation.updatedAt)}</Text>
          </Field>
        </SimpleGrid>

        {metadataKeys.length > 0 && (
          <Accordion variant="contained">
            <Accordion.Item value="metadata" role="region">
              <Accordion.Control>
                <Text size="textXs" c="dimmed">
                  Metadata ({metadataKeys.length} {metadataKeys.length === 1 ? 'key' : 'keys'})
                </Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Code block style={{ fontSize: 11 }}>
                  {JSON.stringify(conversation.metadata, null, 2)}
                </Code>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        )}
      </Stack>
    </Paper>
  );
}
